import React, { useState, useEffect } from 'react';
import Header from '../components/menu/header';
import Footer from '../components/menu/footer';
import './lc.css';

const LC = () => {
  const [activeSection, setActiveSection] = useState('');
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [isMobile, setIsMobile] = useState(false);
  
  // Controlla se siamo su mobile
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Inizializza lo stato delle sezioni solo una volta
  useEffect(() => {
    const defaultExpanded = {};
    const mobile = window.innerWidth <= 768;
    tocData.forEach(section => {
      defaultExpanded[section.id] = !mobile; // Espanse su desktop, collassate su mobile
    });
    setExpandedSections(defaultExpanded);
  }, []); // Array vuoto per eseguire solo al mount

  // Gestione scroll e evidenziazione sezione attiva
  useEffect(() => {
    const handleScroll = () => {
      // Mostra/nascondi bottone scroll to top
      setShowScrollTop(window.pageYOffset > 300);

      // Trova la sezione corrente
      const sections = document.querySelectorAll('.lc-section[id]');
      let currentSection = '';
      let bestMatch = null;
      let bestDistance = Infinity;

      sections.forEach(section => {
        const rect = section.getBoundingClientRect();
        const distance = Math.abs(rect.top - 100);
        
        if (rect.top <= 150 && rect.bottom >= 50 && distance < bestDistance) {
          bestDistance = distance;
          bestMatch = section.id;
        }
      });

      if (bestMatch) {
        currentSection = bestMatch;
      }

      setActiveSection(currentSection);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Auto scroll del menu per mantenere l'elemento attivo visibile
  useEffect(() => {
    if (activeSection) {
      const timer = setTimeout(() => {
        const activeLink = document.querySelector(`.lc-toc a[href="#${activeSection}"]`);
        const tocContainer = document.querySelector('.lc-toc');
        
        if (activeLink && tocContainer) {
          const linkRect = activeLink.getBoundingClientRect();
          const containerRect = tocContainer.getBoundingClientRect();
          
          const linkOffsetTop = activeLink.offsetTop;
          const containerScrollTop = tocContainer.scrollTop;
          const containerHeight = containerRect.height;
          
          const linkTopInView = linkOffsetTop - containerScrollTop;
          const linkBottomInView = linkTopInView + linkRect.height;
          
          const topMargin = 40;
          const bottomMargin = 40;
          
          const isAboveView = linkTopInView < topMargin;
          const isBelowView = linkBottomInView > containerHeight - bottomMargin;
          
          if (isAboveView || isBelowView) {
            let targetScrollTop;
            
            if (isAboveView) {
              targetScrollTop = linkOffsetTop - topMargin;
            } else {
              targetScrollTop = linkOffsetTop - containerHeight + linkRect.height + bottomMargin;
            }
            
            targetScrollTop = Math.max(0, targetScrollTop);
            
            if (Math.abs(containerScrollTop - targetScrollTop) > 10) {
              tocContainer.scrollTo({
                top: targetScrollTop,
                behavior: 'smooth'
              });
            }
          }
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [activeSection]);

  // Funzione per scroll smooth
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      // Se la sezione è collassata, espandila prima
      if (!expandedSections[sectionId]) {
        toggleSection(sectionId);
        // Aspetta un po' per l'animazione di espansione
        setTimeout(() => {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }, 300);
      } else {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    }
  };

  // Funzione per tornare in cima
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // Funzione per toggle sezioni
  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const handleDownloadPDF = (filename) => {
    const link = document.createElement('a');
    link.href = `/documents/${filename}`;
    link.download = filename;
    link.target = '_blank'; // Fallback: apre in nuova tab
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Dati per l'indice
  const tocData = [
    { id: 'introduzione', title: 'Introduzione' },
    { id: 'a-chi-e-rivolta', title: 'A chi è rivolta' },
    { id: 'tempistiche', title: 'Tempistiche' },
    { id: 'da-fare-prima', title: 'Da fare prima' },
    { id: 'lattivita', title: 'L\'Attività' },
    { id: 'il-lancio', title: 'Il Lancio' },
    { id: 'divisione-squadre', title: 'Divisione squadre e regole' },
    { id: 'le-partite', title: 'Le partite' },
    { id: 'sfida-finale', title: 'La sfida finale' },
    { id: 'considerazioni-finali', title: 'Considerazioni Finali' }
  ];

  // Componente per renderizzare l'indice
  const TableOfContents = () => (
    <nav className="lc-toc">
      <ul>
        {tocData.map((section) => (
          <li key={section.id}>
            <a
              href={`#${section.id}`}
              className={activeSection === section.id ? 'active' : ''}
              onClick={(e) => {
                e.preventDefault();
                scrollToSection(section.id);
              }}
            >
              {section.title}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );

  // Componente per sezione collassabile
  const CollapsibleSection = ({ id, title, icon, children, variant = 'default' }) => {
    const isExpanded = expandedSections[id];
    const sectionClass = `lc-section ${variant !== 'default' ? `lc-${variant}` : ''}`;

    return (
      <section id={id} className={`${sectionClass} ${isExpanded ? 'expanded' : 'collapsed'}`}>
        <div 
          className="lc-section-header clickable"
          onClick={() => toggleSection(id)}
        >
          <h2>
            <i className={icon}></i>
            {title}
          </h2>
          <div className="lc-toggle-icon">
            <i className={`fas ${isExpanded ? 'fa-chevron-up' : 'fa-chevron-down'}`}></i>
          </div>
        </div>
        <div className={`lc-section-content ${isExpanded ? 'expanded' : 'collapsed'}`}>
          <div className="lc-content">
            {children}
          </div>
        </div>
      </section>
    );
  };

  return (
    <div className="lc-page">
      <Header />

      {/* Hero Section */}
      <section className="lc-hero">
        <div className="lc-hero-content">
          <h1><div className="lc-rhino-icon">🦏</div> I CORNI GRIGI</h1>
          <p>Avviamento al gioco dello scoutball - Format di Attività L/C</p>
          <button onClick={() => handleDownloadPDF('corni.pdf')} className="lc-download-btn">
            <i className="fas fa-download"></i>
            Scarica PDF
          </button>
        </div>
      </section>

      {/* Container principale */}
      <div className="lc-container-wrapper">
        {/* Sidebar con indice */}
        <aside className="lc-sidebar">
          <div className="lc-toc-header">
            <i className="fas fa-list"></i>
            INDICE
          </div>
          <TableOfContents />
        </aside>

        {/* Main Content */}
        <main className="lc-main">
          <div className="lc-container">
            
            {/* Introduzione */}
            <CollapsibleSection 
              id="introduzione" 
              title="Introduzione" 
              icon="fas fa-info-circle"
            >
              <p>
                La pattuglia scoutball della Zona di Bologna, da molto tempo impegnata in attività inerenti allo scoutball per branche 
                E/G, R/S e CoCa ha voluto creare un'attività disegnata specificatamente per l'ambiente fantastico della branca L/C. 
                Non è stata pensata come un "torneo di scoutball", si avvicina probabilmente più ad un grande gioco, dove lo 
                scoutball viene utilizzato sostanzialmente come strumento principale dell'attività.
              </p>
            </CollapsibleSection>

            {/* A chi è rivolta */}
            <CollapsibleSection 
              id="a-chi-e-rivolta" 
              title="A chi è rivolta" 
              icon="fas fa-users"
            >
              <p>
                L'attività è ideata su due branchi, quindi su una stima di 50 bambini circa. I Vecchi lupi coprono un ruolo 
                fondamentale sia nella fase di preparazione che nel giorno dell'attività vera e propria. Chiaramente il tutto diventa 
                facilmente adattabile ad un solo branco o anche a 3 o 4 branchi contemporaneamente (e oltre...).
              </p>
              <p>
                È prevista la presenza di almeno 6 "corni grigi", tipicamente membri della pattuglia o, in alternativa, presi dalle staff 
                di branco.
              </p>
              <p>
                I riferimenti che troverete all'interno di questo documento sono relativi all'ambiente fantastico per il branco dei 
                lupetti, ovviamente con semplici correzioni è possibile adattare il tutto al Cerchio delle cocci o pensare ad un 
                ambiente "ibrido".
              </p>
              <p>
                Per come è strutturato il gioco e soprattutto per le finalità educative che vogliamo cercare di raggiungere, 
                consigliamo che a ricoprire il ruolo di "corni grigi" siano i membri della pattuglia scoutball o qualsiasi altre persone 
                che non coprano un ruolo attivo all'interno delle staff.
              </p>
            </CollapsibleSection>

            {/* Tempistiche */}
            <CollapsibleSection 
              id="tempistiche" 
              title="Tempistiche" 
              icon="fas fa-clock"
            >
              <p>
                Non esiste una tempistica precisa, l'attività per 50 bambini richiede una mezza giornata scarsa, ma aumentando il 
                numero di bambini e la durata delle fasi del gioco (più avanti illustrate), si può arrivare a coprire anche una giornata 
                intera.
              </p>
            </CollapsibleSection>

            {/* Da fare prima */}
            <CollapsibleSection 
              id="da-fare-prima" 
              title="Da fare prima" 
              icon="fas fa-list-check"
            >
              <ul>
                <li>
                  Trovare un bel prato su cui giocare, possibilmente senza buche. Bisognerà avere la possibilità di montare 
                  almeno 3 campi da scoutball, (la lunghezza di ogni singolo campo è ridotta rispetto alle misure 
                  regolamentari, quindi ogni campo può misurare circa 25x18mt (invece che 30x18mt).
                </li>
                <li>
                  Procurarsi il materiale per costruire i campi di gioco, va bene anche il nastro bianco e rosso da "lavori in 
                  corso", i pali delle porte (ottimi i manici di scopa in legno) e un numero di palle uguale o superiore al numero 
                  di squadre in campo. In alternativa La Pattuglia di Zona mette a disposizione il materiale per chi ne facesse 
                  richiesta preventiva.
                </li>
                <li>Assicurarsi di avere eventuali permessi se si gioca su suolo pubblico.</li>
                <li>
                  Avere disponibilità di acqua potabile, di servizi igienici, della cassetta di pronto soccorso, di un ricovero in 
                  caso di brutto tempo.
                </li>
                <li>
                  Assicurarsi di avere a disposizione almeno un fazzolettone da gioco (scalpo) per ogni ragazzo più almeno 7 
                  ulteriori scalpi per ogni corno grigio (seguirà spiegazione...).
                </li>
                <li>Procurarsi almeno un fischietto per arbitrare in ogni campo</li>
                <li>
                  Una riunione di staff preventiva, in cui pianificare la divisione in squadre dei lupi, banalmente in sestiglie, 
                  oppure creando squadre ad hoc in base alle esigenze della staff ( 7 o 8 lupi per squadra, massimo 9).
                </li>
                <li>
                  Nella riunione si verificano le "cose da fare" di questo elenco. Se nell'attività parteciperanno elementi 
                  esterni è bene che siano presenti.
                </li>
                <li>
                  Nel giorno programmato per l'attività, i campi da gioco devono essere montati in anticipo, prima dell'inizio 
                  dell'attività!
                </li>
              </ul>
            </CollapsibleSection>

            {/* L'Attività */}
            <CollapsibleSection 
              id="lattivita" 
              title="L'Attività" 
              icon="fas fa-play"
            >
              <p>
                L'intera attività si costruisce su 4 fasi principali, le tempistiche di ogni fase non sono definite a priori, ma possono 
                variare in base al numero dei lupi e a quanto ci divertiamo. Nell'ordine abbiamo quindi:
              </p>
              <div className="lc-phases">
                <div className="lc-phase">
                  <div className="lc-phase-number">1</div>
                  <div className="lc-phase-content">
                    <h3>Il lancio</h3>
                  </div>
                </div>
                <div className="lc-phase">
                  <div className="lc-phase-number">2</div>
                  <div className="lc-phase-content">
                    <h3>La divisione per squadre, l'allenamento e le regole</h3>
                  </div>
                </div>
                <div className="lc-phase">
                  <div className="lc-phase-number">3</div>
                  <div className="lc-phase-content">
                    <h3>Le partite</h3>
                  </div>
                </div>
                <div className="lc-phase">
                  <div className="lc-phase-number">4</div>
                  <div className="lc-phase-content">
                    <h3>La sfida con i corni grigi e la chiusura dell'attività</h3>
                  </div>
                </div>
              </div>
            </CollapsibleSection>

            {/* Il Lancio */}
            <CollapsibleSection 
              id="il-lancio" 
              title="Il Lancio" 
              icon="fas fa-rocket"
            >
              <p>
                I corni grigi sono un gruppo di rinoceronti. Abbiamo cercato un animale che non interferisse con i nomi jungla del 
                branco ma che, forse anche un po' forzatamente, spiccasse per imponenza e fisicità. Una felpa o una maglietta 
                grigia, due linee nere sotto gli occhi (tipo i giocatori di football americano) e un rudimentale "corno" fatto in caso da 
                mettersi sul naso sorretto da un elastico possono essere facilmente recuperati e aiutano a creare il personaggio.
              </p>
              <p>
                I corni grigi non hanno nomi comuni (Paolo, Marco, Lucia...) ma sono anch'essi inventati e di fantasia; a seconda di 
                chi li impersonificherà, avremo quindi un Corno Veloce, Corno Sorriso, Corno Rotto, Corno Silente, Corno Alle 
                Scale.... Ovviamente tutti i corni avranno il loro scalpo dietro la schiena.
              </p>
               <div className="lc-dialogue">
                <h4>L'ingresso drammatico</h4>
                <p>
                  I Corni, senza farsi vedere prima, irromperanno nel cerchio dei lupi seduti, di corsa, come una mandria inferocita, 
                  passandosi la palla da scoutball velocemente, alcuni possono saltare fisicamente in mezzo il cerchio, creeranno 
                  scompiglio, ruberanno i cappellini dei lupi (per poi ridarli).
                </p>
                <p>
                  Akela e il branco saranno stupiti e increduli difronte a questa scena e chiederanno di presentarci per capire chi siamo 
                  e cosa ci facciamo lì.
                </p>
              </div>
              <div className="lc-dialogue">
                <h4>Il discorso dei Corni Grigi</h4>
                <p>
                  Dopo le presentazioni, un corno parlerà al branco, dicendo che da tempo osservano i lupi nella jungla, e hanno 
                  notato che sempre più branchi "mancano la preda", sono goffi nei movimenti, non hanno una pelliccia lucente come 
                  un tempo, non sanno più cacciare coordinati e veloci come invece un vero branco sa fare.
                </p>
                <blockquote>
                  "Noi corni grigi siamo custodi di una perfetta tecnica di caccia, infallibile. Siamo arrivati nel vostro territorio per farvi 
                  vedere e insegnare un nuovo modo di cacciare nel branco, una tecnica elegante e avvincente, che vi permetterà di 
                  conquistare qualsiasi preda nella jungla... ma a vedervi adesso mi sembrate più dei cuccioli di lupo che dei lupi veri e 
                  propri,.. forse abbiamo sbagliato branco, forse non siete degni di ascoltare i nostri segreti..."
                </blockquote>
                <p>
                  Akela e il branco ovviamente affermeranno il contrario, diranno di essere pronti ad ascoltare e a imparare le 
                  tecniche che i corni grigi hanno da insegnare.
                </p>
                <blockquote>
                  "Bene allora, vi metteremo alla prova e vedremo quanto il vostro branco è capace di fare..."
                </blockquote>
              </div>
            </CollapsibleSection>

            {/* Divisione squadre e regole */}
            <CollapsibleSection 
              id="divisione-squadre" 
              title="La Divisione per squadre, l'allenamento e le regole" 
              icon="fas fa-users-cog"
            >
              <p>
                I Corni grigi chiederanno al branco di dividersi per gruppi (squadre), con l'aiuto dei vecchi lupi questa operazione 
                diventerà semplice e veloce, in quanto già pensata preventivamente. La cosa importante è che siano il più possibile 
                squadre bilanciate per età e qualità sportive.
              </p>
              <p>
                Ogni corno grigio si posizionerà in un punto poco lontano dal cerchio (magari assieme ad un membro della staff), 
                distanziato dagli altri corni, akela manderà di volta in volta ogni ragazzo dal rispettivo corno grigio, fino a formare le 
                squadre.
              </p>
              
              <h3>Regole semplificate per Lupetti</h3>
              <p>
                Il regolamento si basa sui fondamentali dello scoutball, sintetizzando e semplificando il più possibile le già complesse 
                norme che lo regolano. Lo scopo del gioco rimane ovviamente fare una meta in più della squadra avversaria per 
                vincere la partita ma queste sono le sostanziali differenze rispetto allo scoutball:
              </p>
              <ul className="lc-rules-list">
                <li>Due tempi da 10 minuti ciascuno</li>
                <li>Non esistono aree davanti alla porta, né tantomeno falli di difesa o attacco</li>
                <li>I giocatori scalpati a buono rimangono fuori dal campo per due/tre minuti, per poi rientrare</li>
                <li>Si possono fare infiniti scalpi vuoti</li>
                <li>Non esiste un referto di gara</li>
                <li>I giocatori di ogni squadra, prima di poter fare meta, devono passarsi tra di loro la palla almeno fra tre compagni, nella stessa azione (questo per cercare di far giocare tutti).</li>
                <li>Il numero e/o il sesso dei giocatori in campo per ogni squadra è flessibile e adattabile alle esigenze del branco, tuttavia sconsigliamo di superare i 9 elementi per squadra in campo (possiamo utilizzare i cambi).</li>
              </ul>

              <h3>Regole base del gioco</h3>
              <div className="lc-rules-section">
                <h4>Lo scalpo</h4>
                <p>
                  Ogni giocatore è provvisto di uno scalpo, che deve essere sempre ben visibile. Lo scalpo viene indossato dentro ai 
                  pantaloncini in corrispondenza della schiena in posizione centrale, in modo che le due punte dello scalpo escano dal 
                  pantaloncino di almeno 25 cm, rivolte verso il suolo, senza che ne sia impedita la facile estrazione da nodi o altro.
                </p>
                
                <h4>Il campo di gioco</h4>
                <p>
                  Il campo di gioco è un rettangolo di 25 x 18 m circa con fondo erboso, delimitato da nastri. Le righe perimetrali sono 
                  considerate fuori. Le porte, di larghezza 4 m, sono situate sui lati corti e delimitate da due paletti piantati a terra alti 
                  almeno 1,5 m, liberi di cadere se urtati con forza.
                </p>

                <h4>Scalpo vuoto e scalpo buono</h4>
                <div className="lc-scalpo-rules">
                  <div className="lc-scalpo-buono">
                    <h5>Lo scalpo è buono quando:</h5>
                    <ul>
                      <li>l'attaccante viene scalpato in possesso di palla;</li>
                      <li>l'attaccante lancia la palla in aria, viene scalpato, e successivamente viene a contatto con la palla al volo (anche accidentalmente).</li>
                    </ul>
                  </div>
                  <div className="lc-scalpo-vuoto">
                    <h5>Lo scalpo è vuoto quando:</h5>
                    <ul>
                      <li>l'attaccante viene scalpato e non è in possesso della palla;</li>
                      <li>l'attaccante lascia la palla, viene scalpato e la palla lo colpisce dopo aver rimbalzato per terra;</li>
                    </ul>
                  </div>
                </div>

                <h4>Meta</h4>
                <p>È meta quando un giocatore appoggia la palla a terra oltre la linea di porta avversaria.</p>
                <p>La meta non è valida quando:</p>
                <ul>
                  <li>il giocatore supera la linea di porta con il piede o i piedi a terra, anche dopo aver appoggiato la palla;</li>
                  <li>il giocatore non riesce a trattenere la palla in posizione fino alla convalida dell'arbitro;</li>
                  <li>il giocatore commette fallo di sfondamento;</li>
                  <li>il giocatore prende la palla dopo che questa ha già superato la linea di porta, ossia in fallo di fondo.</li>
                </ul>
              </div>

              <h3>Allenamento</h3>
              <p>
                Una volta che ogni corno si trova nella propria metà campo con la sua squadra, comincerà una sorta di allenamento 
                con i ragazzi. Qualsiasi prova di abilità fisico/motoria può andar bene, a patto che sia inerente al gioco che poi i 
                ragazzi andranno effettivamente a fare, qualche esempio:
              </p>
              <ul>
                <li>Prendere al volo un pallone mentre si corre</li>
                <li>Correre passandosi il pallone (magari in due/tre per volta)</li>
                <li>Fare un cerchio dove tutti se la passano e un ragazzo in mezzo tenta di prenderla (tipo "torello")</li>
              </ul>
            </CollapsibleSection>

            {/* Le partite */}
            <CollapsibleSection 
              id="le-partite" 
              title="Le partite" 
              icon="fas fa-trophy"
            >
              <p>
                Non è un torneo nel senso più classico del termine, non ci sono fasi ad eliminazione, niente quarti, semifinali o finale, 
                niente premi per il miglior scalpatore o miglior realizzatore.
              </p>
              <p>
                È qualcosa di più simile ad un girone all'italiana, dove ogni squadra sfiderà le altre, ruotandosi fra i vari campi.
              </p>
              <p>
                Su ogni campo è bene che siano presenti i corni grigi assegnati ad ogni squadra, che saranno gli arbitri. Ogni 
                componente della staff può essere assegnato ad una squadra, aiutare i corni nella gestione della partita e addirittura 
                giocare, se ce ne sia la necessità.
              </p>
            </CollapsibleSection>

            {/* La sfida finale */}
            <CollapsibleSection 
              id="sfida-finale" 
              title="La sfida con i corni grigi e la chiusura dell'attività" 
              icon="fas fa-fist-raised"
            >
              <p>Una volta terminate le partite si chiamerà cerchio tutti insieme.</p>
              <p>
                Con il branco seduto, i corni grigi torneranno in mezzo al cerchio, il corno grigio che ha parlato nel lancio riprenderà 
                la parola:
              </p>
              <blockquote>
                "Abbiamo visto il vostro impegno e possiamo dire che ve la siete cavata, siete abbastanza in gamba e avete imparato 
                a giocare di squadra e ad aiutarvi uno con l'altro. Tuttavia non siete certo dei campioni come noi e avete ancora 
                tanta strada da fare, come ben sapete noi siamo i custodi di questo gioco e siamo invincibili".
              </blockquote>
              <p>
                Il branco, incoraggiato da Akela e i vecchi lupi, non sarà d'accordo con la valutazione dei Corni, dichiarandosi pronti a 
                sfidarli sul campo e a far valere le proprie capacità contro di loro.
              </p>
              
              <h3>Parte così la sfida finale: Branco vs Corni Grigi</h3>
              <div className="lc-final-rules">
                <p>Il campo da gioco è lo stesso degli altri, tuttavia, in base al numero dei lupi presenti, è bene allungare la lunghezza del campo.</p>
                <p>È una partita di scoutball particolare, tutti i corni grigi avranno 8/9 scalpi attorno alla cintura, bisognerà dire ai lupi che l'unico modo che hanno per vincere è fare una meta in più dei corni oppure scalparli tutti.</p>
                <ul>
                  <li>Tutta la partita si svolge su un unico tempo</li>
                  <li>I lupi proveranno a fare meta, ma bisognerà spingerli a scalpare uno per uno tutti gli scalpi di tutti i corni grigi</li>
                  <li>I lupi potranno tentare di togliere uno scalpo ad un corno solo se questo è in possesso della palla (scalpo buono)</li>
                  <li>Quando un corno grigio finisce gli scalpi esce dal campo</li>
                  <li>Ovviamente i corni dovranno a poco a poco farsi scalpare e accettare la sconfitta</li>
                </ul>
              </div>
              <p>
                Con i corni battuti, i lupi tornano in cerchio, i corni accetteranno la sconfitta congratulandosi con il branco e 
                saluteranno.
              </p>
            </CollapsibleSection>

            {/* Considerazioni finali */}
            <CollapsibleSection 
              id="considerazioni-finali" 
              title="Considerazioni Finali" 
              icon="fas fa-lightbulb"
              variant="conclusions"
            >
              <ul>
                <li>Si può prevedere, terminato lo scontro finale, un momento di merenda tutti assieme, i corni possono portare una torta che mangeranno con il branco vittorioso.</li>
                <li>Il branco, chiaramente con anticipo, può organizzare un'attività per farsi le divise per le squadre, utilizzando colori per stoffa o altre tecniche.</li>
                <li>Con l'occasione di questa attività sportiva, i vecchi lupi possono trovare ulteriori spunti per eventuali "prede" nella progressione personale di ogni ragazzo o nella conquista di specialità specifiche.</li>
                <li>È bene pianificare una riunione di verifica con la staff e i corni grigi, in modo da evidenziare le eventuali problematiche rinvenute e dove possibile migliorare ancor di più questa attività.</li>
              </ul>
              <div className="lc-final-note">
                <p>
                  Tutto quanto descritto rappresenta una linea guida, ogni staff si senta libera di adattarla alle proprie necessità.
                </p>
                <p>
                  I membri della pattuglia Scoutball sono e rimarranno sempre a disposizione.
                </p>
                <p className="lc-buona-caccia"><strong>Buona Caccia!</strong></p>
              </div>
            </CollapsibleSection>

          </div>
        </main>
      </div>

      {/* Bottone scroll to top */}
      <button
        className={`lc-scroll-top ${showScrollTop ? 'visible' : ''}`}
        onClick={scrollToTop}
        aria-label="Torna in cima"
      >
        <i className="fas fa-arrow-up"></i>
      </button>

      <Footer />
    </div>
  );
};

export default LC;